import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'

import { connect } from 'react-redux'

import {
  Box,
  Flex,
  LoadingSection,
} from 'app/components/design-system-components'
import { SearchRounded } from 'app/components/design-system-components/icons/actions'
import { Input } from 'app/components/design-system-components/inputs'
import Pagination from 'app/components/design-system-components/Pagination'
import { fontWeight } from 'app/components/design-system-components/styles/variables'
import UpgradeCTA from 'app/components/design-system-components/UpgradeCTA'
import { translateErrorCodeToMessage } from 'app/constants/error_messages'
import * as actFilters from 'app/dashboard/team_activity/ActivityFilters'
import { activityPeriods } from 'app/dashboard/team_activity/ActivityFilters/DateRangeFilter/constants'
import EmailTable from 'app/dashboard/team_activity/EmailReporting/EmailTable'
import MultiTeamReportingDropdown from 'app/dashboard/team_activity/multi_team_reporting_dropdown/component'
import { debounce, STANDARD_TIMEOUT } from 'app/helpers/debounce'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { listEmailLogs } from 'app/services/http/email'
import * as snugNotifier from 'app/services/snugNotifier'
import { ACCOUNT_TYPE_LITE } from 'app/utils/accounts/helpers'
import * as dateTimeHelpers from 'app/utils/datetime/helpers'
import { useStateWithLoading } from 'app/utils/hooks/useStateWithLoading'
import {
  filterReducerFactory,
  filtersActions,
} from 'app/utils/reducers/filtersReducer'

const PAGINATION_LIMIT = 8

interface FiltersAndSearchProps {
  filtersState: any
  filtersDispatch: any
  currentTeam: any
  onFiltersInitialized: any
}

const FiltersAndSearch = ({
  filtersState,
  filtersDispatch,
  currentTeam,
  onFiltersInitialized,
}: FiltersAndSearchProps) => {
  const [searchTextState, setSearchTextState] = useState('')

  const debouncedOnSearchTextChange = useCallback(
    debounce(
      (value: string) => filtersDispatch(filtersActions.updateSearch(value)),
      STANDARD_TIMEOUT,
    ),
    [],
  )

  const onSearchTextChange = ({
    target: { value },
  }: {
    target: { value: string }
  }) => {
    setSearchTextState(value)
    debouncedOnSearchTextChange(value)
  }

  const filtersInputs = (
    <Box ml={2}>
      <actFilters.ActivitiesFilters
        teamId={currentTeam.guid}
        initialPeriod={activityPeriods.currentMonth}
        filters={filtersState.filters}
        updateFilter={(filterId: string, filterValues: any) =>
          filtersDispatch(filtersActions.updateFilter(filterId, filterValues))
        }
        onFiltersInitialized={onFiltersInitialized}
        hasMultiTeamFilter={false}
        includePodsFilter={false}
        managers={undefined}
        pods={undefined}
        includeManagersFilter={undefined}
        onPeriodChange={undefined}
      />
    </Box>
  )

  const renderMultipleReportingFilter = (
    <MultiTeamReportingDropdown
      teamId={currentTeam.guid}
      multiTeamOptions={{}}
      updateFilter={(filterId: string, filterValues: any) =>
        filtersDispatch(filtersActions.updateFilter(filterId, filterValues))
      }
      dropDownWidth="145px"
      isOnlyMultiTeamReportingFilter={undefined}
    />
  )

  return (
    <Box>
      <Flex flexWrap="wrap">
        <Input
          icon={
            <SearchRounded
              style={{
                color: theme.colors.gray400,
              }}
            />
          }
          inputProps={{
            placeholder: 'Search name, email, type, subject',
            value: searchTextState,
            onChange: onSearchTextChange,
            height: theme.height[5] + 'px',
            fontSize: theme.fontSizes.pSmall12,
          }}
          containerProps={{
            marginBottom: theme.space[4] + 'px',
          }}
          maxWidth={theme.width[23] + 'px'}
          height={theme.height[18] + 'px'}
          id={undefined}
          prefix={undefined}
          postfix={undefined}
          endedIcon={undefined}
        />
        {filtersInputs}
        {renderMultipleReportingFilter}
      </Flex>
    </Box>
  )
}

interface EmailListProps {
  currentTeam: any
  isEnabled: any
}

const EmailList = ({ currentTeam }: EmailListProps) => {
  const {
    state: emailList,
    setState: setEmailList,
    loadingStates: listLoadingStates,
    loadingStatesHelpers: setListLoadingStatesHelpers,
  } = useStateWithLoading([])

  const [filtersState, filtersDispatch] = useReducer(
    filterReducerFactory(actFilters.filtersInitialState),
    actFilters.filtersInitialState,
  )

  const [filtersInitializationReady, setFiltersInitializationReady] =
    useState(false)

  const [paginationOffset, setPaginationOffset] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const { accountType = ACCOUNT_TYPE_LITE } = currentTeam || {}

  const isAccountLite = accountType === ACCOUNT_TYPE_LITE

  useEffect(() => {
    setPaginationOffset(0)
  }, [filtersState])

  useEffect(() => {
    if (filtersInitializationReady) {
      loadEmail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationOffset, filtersState])

  const loadEmail = () => {
    setListLoadingStatesHelpers.startLoading()
    const { range = {}, teams } = actFilters.getFilters(filtersState)
    const formattedRange = dateTimeHelpers.formatFilterRangeToUTC(range)
    const teamsIds =
      teams?.map((team: any) => team?.agencyInfo?.agencyGUID) || []

    return listEmailLogs(currentTeam.guid, {
      offset: paginationOffset,
      limit: PAGINATION_LIMIT,
      ...formattedRange,
      q: filtersState.search,
      team: teamsIds,
    })
      .then(({ items, count }: any) => {
        setEmailList(items)
        setTotalCount(count)
        setListLoadingStatesHelpers.markDoneSuccessfully()
      })
      .catch((err: any) => {
        snugNotifier.error(translateErrorCodeToMessage(err))
        setListLoadingStatesHelpers.setError(err)
      })
  }

  const onChangePage = (selectedPage: number) => {
    setPaginationOffset(selectedPage * PAGINATION_LIMIT)
  }

  const searchAndFiltersElem = (
    <FiltersAndSearch
      currentTeam={currentTeam}
      filtersState={filtersState}
      filtersDispatch={filtersDispatch}
      onFiltersInitialized={() => setFiltersInitializationReady(true)}
    />
  )

  const totalCountElem = (count: number) => (
    <Box
      py={3}
      px={5}
      borderRadius={2}
      color={theme.colors.gray600}
      fontSize={5}
      fontWeight={fontWeight[6]}
      lineHeight={1.4}
      backgroundColor={theme.colors.paleBlue}
      variant="blackWithBlueBg"
    >
      Total: {count}
    </Box>
  )

  const emptyStateMessage = <Box>No results</Box>

  const currentPage = Math.floor(paginationOffset / PAGINATION_LIMIT) + 1
  const pagesCount = Math.ceil(totalCount / PAGINATION_LIMIT)
  const paginationElem =
    !listLoadingStates.error && pagesCount > 1 ? (
      <Pagination
        canNextPage={pagesCount > currentPage}
        canPreviousPage={currentPage > 1}
        currentPageNum={currentPage}
        maxPageNumbers={pagesCount}
        onPaginationButtonClicked={onChangePage}
      />
    ) : null

  const memoizedTable = useMemo(
    () => <EmailTable emailListData={emailList} />,
    [emailList],
  )

  return (
    <div>
      <Flex justifyContent="space-between" alignItems="center" mb={5}>
        <Flex flexDirection="column" flex="1">
          {searchAndFiltersElem}
        </Flex>
        <Flex alignItems="center" mb={4}>
          {totalCountElem(totalCount)}
        </Flex>
      </Flex>
      {isAccountLite ? (
        <UpgradeCTA
          availableText="This feature is available on"
          availableOn="paid plans"
          alignRight={false}
          maxWidth="fit-content"
          textContainerProps={{
            display: 'flex',
            alignItems: 'center',
            lineHeight: '1.3',
          }}
          learnmoreVariant="linkBlueWithoutWeight"
        />
      ) : (
        <>
          <LoadingSection
            loadingState={listLoadingStates}
            actionHandler={loadEmail}
            loaderProps={{ fontSize: '48px' }}
          >
            {memoizedTable}
            {!emailList?.length && (
              <Box
                textAlign="center"
                lineHeight={1.4}
                m="auto"
                mt={8}
                width="30%"
              >
                {emptyStateMessage}
              </Box>
            )}
          </LoadingSection>

          {paginationElem}
        </>
      )}
    </div>
  )
}

const mapStateToProps = ({ session }: any) => {
  return {
    currentTeam: session.currentTeam,
    currentAgency: session.currentAgency,
  }
}

export const EmailListPage = connect(mapStateToProps)(EmailList)
